import type { QuotaRead } from '../../types/api';

interface HomeStatsProps {
  /** Needs-you count as shown ("3", "50+"), or null while unknown. */
  waiting: string | null;
  isWaiting: boolean;
  inProgress: number | null;
  plannedThisWeek: number | null;
  /** The last day the week covers, already worded ("Wed, Oct 7"). */
  through: string;
  quota: QuotaRead | null;
}

/** An unknown number. Never a zero, which would be a claim. */
const UNKNOWN = '--';

/**
 * Home's four numbers at a glance. Every one comes from a read Home already
 * makes; a number that failed to load reads `--` and nothing gates on it.
 */
export function HomeStats({
  waiting,
  isWaiting,
  inProgress,
  plannedThisWeek,
  through,
  quota,
}: HomeStatsProps) {
  const usedPct = quota
    ? Math.min(100, Math.round((quota.used / Math.max(1, quota.limit)) * 100))
    : 0;
  const meterTone =
    quota === null
      ? ''
      : quota.remaining === 0
        ? ' home-stat__meter--empty'
        : quota.remaining / Math.max(1, quota.limit) < 0.2
          ? ' home-stat__meter--low'
          : '';

  return (
    <ul className="home-stats" aria-label="At a glance">
      <li className="home-stat">
        <span className="home-stat__label">Needs you</span>
        <span className={`home-stat__value${isWaiting ? ' home-stat__value--warn' : ''}`}>
          {waiting ?? UNKNOWN}
        </span>
        <span className="home-stat__hint">
          {waiting === null || isWaiting ? 'Posts to review, and any that failed' : 'All clear'}
        </span>
      </li>
      <li className="home-stat">
        <span className="home-stat__label">In progress</span>
        <span className="home-stat__value">{inProgress ?? UNKNOWN}</span>
        <span className="home-stat__hint">Being written and designed</span>
      </li>
      <li className="home-stat">
        <span className="home-stat__label">Planned this week</span>
        <span className="home-stat__value">{plannedThisWeek ?? UNKNOWN}</span>
        <span className="home-stat__hint">Through {through}</span>
      </li>
      <li className="home-stat">
        <span className="home-stat__label">Posts left</span>
        <span
          className={`home-stat__value${quota?.remaining === 0 ? ' home-stat__value--empty' : ''}`}
        >
          {quota ? `${quota.remaining} / ${quota.limit}` : `${UNKNOWN} / ${UNKNOWN}`}
        </span>
        <span className="home-stat__hint">Resets on the 1st</span>
        {/* The words above carry the number; the meter is a picture of it. */}
        <span className={`home-stat__meter${meterTone}`} aria-hidden="true">
          <span style={{ width: `${usedPct}%` }} />
        </span>
      </li>
    </ul>
  );
}
